import { useState } from 'react';
import { resendEmailVerification, type ResendVerificationResult } from '../../api/emailVerification';
import { useOptionalAuth } from '../../auth/AuthProvider';
import { useT } from '../../i18n';
import styles from './EmailVerifyBanner.module.css';

const DISMISS_KEY = 'lorath.emailVerifyBanner.dismissed';

function readDismissed(): boolean {
  try { return window.sessionStorage.getItem(DISMISS_KEY) === '1'; } catch { return false; }
}
function writeDismissed(): void {
  try { window.sessionStorage.setItem(DISMISS_KEY, '1'); } catch { /* Speicher gesperrt: nur diese Ansicht */ }
}

type Phase = 'idle' | 'sending' | ResendVerificationResult;

const RESULT_KEYS: Record<Exclude<Phase, 'idle' | 'sending'>, string> = {
  sent: 'verify_email.banner_sent',
  already_verified: 'verify_email.settings_verified',
  rate_limited: 'verify_email.banner_limit',
  failed: 'verify_email.banner_failed',
};

// F88: Hinweis „E-Mail bestätigen" mit „erneut senden". Erscheint nur, wenn der
// Server die Bestätigung verlangt (EMAIL_VERIFICATION_REQUIRED), das Konto eine
// Adresse hat und sie noch unbestätigt ist. Spielen und Kaufen bleiben möglich;
// das Banner lässt sich für die Sitzung ausblenden.
export function EmailVerifyBanner({ className }: { className?: string }) {
  const { t } = useT();
  const auth = useOptionalAuth();
  const user = auth?.user ?? null;
  const [dismissed, setDismissed] = useState(readDismissed);
  const [phase, setPhase] = useState<Phase>('idle');

  const needs = Boolean(
    auth?.status === 'authed'
    && user
    && user.is_guest !== true
    && user.email_verification_required === true
    && user.email_verified === false
    && typeof user.email === 'string'
    && user.email.trim() !== '',
  );
  if (!needs || dismissed || !user) return null;

  const resend = async () => {
    if (phase === 'sending') return;
    setPhase('sending');
    const result = await resendEmailVerification();
    setPhase(result);
    if (result === 'already_verified') await auth?.refresh().catch(() => undefined);
  };

  const status = phase === 'idle' || phase === 'sending'
    ? t('verify_email.banner_text', { email: user.email })
    : t(RESULT_KEYS[phase]);

  return (
    <div className={[styles.root, className].filter(Boolean).join(' ')} role="status" data-testid="email-verify-banner">
      <span className={styles.text}>{status}</span>
      <span className={styles.actions}>
        {phase !== 'sent' && phase !== 'already_verified' ? (
          <button type="button" className={styles.action} onClick={resend} disabled={phase === 'sending' || phase === 'rate_limited'}>
            {phase === 'sending' ? t('verify_email.banner_sending') : t('verify_email.banner_resend')}
          </button>
        ) : null}
        <button
          type="button"
          className={styles.action}
          onClick={() => { writeDismissed(); setDismissed(true); }}
        >
          {t('verify_email.banner_dismiss')}
        </button>
      </span>
    </div>
  );
}
